import { useCallback, useEffect, useState } from 'react'
import {
  JobDto,
  JobType,
  MY_PLACE_DEFAULT_BODY,
  MY_PLACE_LIVE_CHECKINS_BASELINE_MULTIPLIER,
  MY_PLACE_LIVE_CHECKINS_MIN,
  MY_PLACE_PEEKS_24H_MIN,
  MY_PLACE_PEEKS_DISTINCT_USERS_MIN,
  MY_PLACE_PINNED_LIMIT,
  MY_PLACE_PUSH_MAX_PER_USER_24H,
  MY_PLACE_PUSH_MAX_PER_USER_6H,
  MY_PLACE_PUSH_MAX_PER_VENUE_24H,
  NotificationPresetDto,
  NotificationPresetTrigger,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { jobService } from '../../services/job.service'

const NotificationsMyPlacesTab = () => {
  const [job, setJob] = useState<JobDto | null>(null)
  const [preset, setPreset] = useState<NotificationPresetDto | null>(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    try {
      setIsLoading(true)
      setError('')
      const [jobs, presets] = await Promise.all([
        jobService.getJobs(),
        adminService.getNotificationPresets(),
      ])
      setJob(jobs.find(item => item.type === JobType.EvaluateMyPlacesNotifications) ?? null)
      setPreset(presets.find(item => item.trigger === NotificationPresetTrigger.MyPlacesActivity) ?? null)
    } catch (err) {
      console.log('Failed to load My Places notification config', err)
      setError('Failed to load My Places configuration.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div>
      <p style={{ maxWidth: 720, marginBottom: 20 }}>
        My Places notifies users about fresh activity at venues they pinned (favorites, max {MY_PLACE_PINNED_LIMIT}).
        Copy and enablement are controlled by the My Places preset; pause the job on the Jobs page to stop
        evaluation.
      </p>

      {isLoading && <p>Loading…</p>}
      {error && <p className="text-danger">{error}</p>}

      {!isLoading && !error && (
        <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Job</h4>
            {job ? (
              <ul style={{ marginBottom: 0 }}>
                <li>Name: {job.title}</li>
                <li>Status: {job.status}</li>
                <li>Interval: {job.interval}s</li>
                <li>Next run: {job.nextRunAt ? new Date(job.nextRunAt).toLocaleString() : '—'}</li>
              </ul>
            ) : (
              <p style={{ marginBottom: 0 }}>Evaluate My Places job is not seeded yet. Run migrations.</p>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Preset copy</h4>
            {preset ? (
              <ul style={{ marginBottom: 0 }}>
                <li>Enabled: {preset.enabled ? 'yes' : 'no'}</li>
                <li>Heading: venue name</li>
                <li>Body: {preset.messageTemplate || MY_PLACE_DEFAULT_BODY}</li>
                <li>Deep link: {preset.deepLinkTarget || 'venue'}</li>
              </ul>
            ) : (
              <p style={{ marginBottom: 0 }}>No MyPlacesActivity preset found. Create one on the Presets tab.</p>
            )}
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Triggers (OLTP)</h4>
            <ul style={{ marginBottom: 0 }}>
              <li>Live check-ins (1h) ≥ {MY_PLACE_LIVE_CHECKINS_MIN} and ≥ {MY_PLACE_LIVE_CHECKINS_BASELINE_MULTIPLIER}× hour-of-week baseline</li>
              <li>Peeks (24h) ≥ {MY_PLACE_PEEKS_24H_MIN} with ≥ {MY_PLACE_PEEKS_DISTINCT_USERS_MIN} distinct users</li>
              <li>New RSVPs on upcoming events, new breadcrumbs, or vibe sets at the venue (delta vs last snapshot)</li>
            </ul>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h4 style={{ marginTop: 0 }}>Suppressions</h4>
            <ul style={{ marginBottom: 0 }}>
              <li>User currently checked in anywhere</li>
              <li>User is the actor who created the activity</li>
              <li>At most {MY_PLACE_PUSH_MAX_PER_USER_6H} push / user / 6h and {MY_PLACE_PUSH_MAX_PER_USER_24H} / 24h</li>
              <li>At most {MY_PLACE_PUSH_MAX_PER_VENUE_24H} push / (user, venue) / 24h</li>
              <li>Venue inactive for 30 days is skipped</li>
              <li>3 ignored sends in a row → 7-day cooldown</li>
              <li>User-level opt-out on the mobile My Places screen</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationsMyPlacesTab
